import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Search, Refresh } from '@mui/icons-material';
import HeaderHelpPage from '../../../components/HeaderHelpPage';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { Log, sendDataWithPost } from '../../../services/services';
import { showErrorMsg, showSuccessMsg } from '../../../utils/toast';
import api from '../../../services/api';
import CircularLoader from '../../../components/circularLoader';
import { COLORS } from '../../../utils/colors';
import ButtonCustom from '../../../components/ButtonCustom';
import { styles } from '../../../utils/styles';
import { OXYGEN7_ENTITIES, DEFAULT_OXYGEN7_ENTITY } from '../../../utils/constants';
import ShowDetail from '../../../components/ShowDetail';
import { StorageUtils } from '../../../utils';
import { APP_USER_SESSION } from '../../../constant/AppConstants';

const Outgoing = () => {
  const userSession = StorageUtils.getParsedValue(APP_USER_SESSION);
  const userEntity = userSession?.oxygen7_entity || null;
  const availableOxygen7Entities = userEntity 
    ? OXYGEN7_ENTITIES.filter(opt => opt.value === userEntity || opt.sessionEntity === userEntity) 
    : OXYGEN7_ENTITIES;

  const [entity, setEntity] = useState(availableOxygen7Entities[0]?.value || DEFAULT_OXYGEN7_ENTITY);
  const [payoutDate, setPayoutDate] = useState(moment().toDate());
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [confirm, setConfirm] = useState({ open: false, currency: '', breakdown_group: '', report_name: '', variant: 'bank' }); // variant: 'bank' | 'internal'
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState({ settlements: [], adjustments: [], counts: { settlements: 0, adjustments: 0 } });
  const [detailHeader, setDetailHeader] = useState({ be: '', account: '', currency: '' });

  const payoutDateString = useMemo(() => moment(payoutDate).format('YYYY-MM-DD'), [payoutDate]);

  const loadData = async () => {
    setLoading(true);
    const url = '/payout/report/outgoing_summary/';
    const payload = { payout_date: payoutDateString, registered_country: entity };
    Log.info('Load outgoing summary', payload);
    await sendDataWithPost(url, payload, (data) => setSections(Array.isArray(data) ? data : []), () => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSearch = () => {
    loadData();
  };

  const handleReset = () => {
    setPayoutDate(moment().toDate());
  };

  const handleDownload = async (currency, breakdown_group) => {
    try {
      const url = '/payout/report/download_file/';
      const sec = sections.find(s => s.currency === currency && (s.breakdown_group || '') === (breakdown_group || '')) || {};
      const path = confirm.variant === 'internal' ? sec.internal_report_path : sec.bank_report_path;
      const name = confirm.variant === 'internal' ? (sec.internal_report_name || sec.bank_report_name) : sec.bank_report_name;
      if (!path) {
        showErrorMsg('Report file not available');
        return;
      }
      const registeredCountry = sec.registered_country || entity;
      const response = await api.post(url, { 
        report_path: path, 
        file_name: name,
        registered_country: registeredCountry
      }, { responseType: 'blob' });
      if (response.status === 200) {
        const blob = response.data;
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = name || `report_${currency}_${payoutDateString}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
        showSuccessMsg('Download completed');
      } else {
        showErrorMsg(`Download failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      if (error.response?.status === 401) showErrorMsg('Authorization failed. Please login again.');
      else if (error.response?.status === 404) showErrorMsg('File not found or endpoint unavailable.');
      else if (error.response?.status === 403) showErrorMsg('Access denied.');
      else showErrorMsg(error.response?.data?.detail || error.message || 'Download failed');
    }
  };

  const openConfirm = (currency, breakdown_group, variant = 'bank') => {
    const sec = sections.find(s => s.currency === currency && (s.breakdown_group || '') === (breakdown_group || '')) || {};
    const name = variant === 'internal' ? (sec.internal_report_name || sec.bank_report_name) : sec.bank_report_name;
    setConfirm({ open: true, currency, breakdown_group, report_name: name || '', variant });
  };
  const closeConfirm = () => setConfirm({ open: false, currency: '', breakdown_group: '', report_name: '', variant: 'bank' });
  const confirmDownload = async () => {
    const { currency, breakdown_group } = confirm;
    closeConfirm();
    await handleDownload(currency, breakdown_group);
  };

  const openDetail = async (row) => {
    try {
      setDetailHeader({ be: row.business_entity_name || '-', account: row.account_number || '-', currency: row.currency || '-' });
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailData({ settlements: [], adjustments: [], counts: { settlements: 0, adjustments: 0 } });
      const url = '/payout/report/outgoing_detail/';
      const payload = {
        payout_date: payoutDateString,
        transaction_currency: row.currency,
        account_number: row.account_number,
        merchant_ids: Array.isArray(row.merchant_ids) ? row.merchant_ids : undefined
      };
      const resp = await api.post(url, payload);
      if (resp.status === 200) {
        const data = resp.data || {};
        const settlements = Array.isArray(data.settlements) ? data.settlements : [];
        const adjustments = Array.isArray(data.adjustments) ? data.adjustments : [];
        setDetailData({
          settlements,
          adjustments,
          counts: { settlements: settlements.length, adjustments: adjustments.length }
        });
      } else {
        showErrorMsg(`Load detail failed: ${resp.status}`);
      }
    } catch (e) {
      showErrorMsg(e?.response?.data?.detail || e.message || 'Load detail failed');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setDetailOpen(false);

  const combinedRows = useMemo(() => {
    const s = (detailData.settlements || []).map((x) => ({ ...x, _variant: 'settlement' }));
    const a = (detailData.adjustments || []).map((x) => ({ ...x, _variant: 'adjustment' }));
    return [...s, ...a];
  }, [detailData]);

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <HeaderHelpPage title="Outgoing" isShowLink={false} />
      <Card sx={{ mb: 2, px: 2, py: 2, borderRadius: 2, border: '1px solid #f0f0f0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Oxygen7 Entity
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={entity}
                  onChange={(e) => setEntity(e.target.value)}
                  size="small"
                  displayEmpty
                  sx={{
                    height: '30px',
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                    }
                  }}
                >
                  {availableOxygen7Entities.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Payout Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  disableFuture
                  inputFormat="YYYY-MM-DD"
                  value={payoutDate}
                  onChange={(v) => v && setPayoutDate(v?.toDate ? v.toDate() : v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiFormLabel-root': {
                          lineHeight: '14px',
                          fontSize: '14px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                          height: '14px',
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} />
        </Grid>

        <Stack direction="row" justifyContent="flex-end">
          <ButtonCustom
            sx={{ ml: 1, mt: 2, ...styles.customButton }}
            icon={<Search />}
            variant="contained"
            title="Search"
            onClick={handleSearch}
          />
          <ButtonCustom
            sx={{ ml: 1, mt: 2 }}
            icon={<Refresh />}
            title="Reset"
            onClick={handleReset}
          />
        </Stack>
      </Card>

      {loading ? (
        <CircularLoader />
      ) : sections.length === 0 ? (
        <Card sx={{ p: 4, borderRadius: 2, border: '1px solid #f0f0f0' }}>
          <Typography variant="h6" align="center" sx={{ py: 4, fontSize: '16px', fontWeight: 600 }}>
            Not found
          </Typography>
        </Card>
      ) : sections.map((sec) => {
        const isEmptySection = (!sec.bank_report_name && !sec.bank_report_path && (!sec.rows || sec.rows.length === 0));
        return (
          <Card key={`${sec.currency}-${sec.breakdown_group}`} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #f0f0f0' }}>
          {!isEmptySection && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Typography variant="h6">
                Settlement - {sec.currency} {sec.breakdown_group && sec.breakdown_group !== 'DEFAULT' && sec.rows?.length > 0 ? `(${sec.rows[0].business_entity_name})` : ''}
              </Typography>
              <Typography
                component="span"
                sx={{ 
                  textDecoration: 'underline', 
                  cursor: sec.bank_report_path ? 'pointer' : 'default',
                  color: sec.bank_report_path ? COLORS.LINK_BLUE : 'error.main'
                }}
                onClick={sec.bank_report_path ? () => openConfirm(sec.currency, sec.breakdown_group, 'bank') : undefined}
              >
                Download Bank (SCB) CSV
              </Typography>
            </Box>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 60 }}>S/N</TableCell>
                  <TableCell>Business Name</TableCell>
                  <TableCell>Bank Swift Code</TableCell>
                  <TableCell>Bank Account Number</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell align="right">Total Amount</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(sec.rows) && sec.rows.length > 0 && (
                  sec.rows.map((row, idx) => (
                  <TableRow key={`${row.business_entity_name}-${row.account_number}-${idx}`}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{row.business_entity_name ?? ''}</TableCell>
                    <TableCell>{row.bank_code ?? ''}</TableCell>
                    <TableCell>{row.account_number ?? ''}</TableCell>
                    <TableCell>{row.currency ?? ''}</TableCell>
                    <TableCell align="right">{row.total_paid_amount == null ? '' : Number(row.total_paid_amount).toLocaleString(undefined, row.currency === 'JPY' ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    <TableCell>
                      <Button
                        size="medium"
                        variant="text"
                        onClick={() => openDetail(row)}
                        sx={{ '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                  ))
                )}
                {Array.isArray(sec.rows) && sec.rows.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} />
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{sec.currency ?? ''}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{sec.total_amount == null ? '' : Number(sec.total_amount).toLocaleString(undefined, sec.currency === 'JPY' ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    <TableCell>
                      <Button
                        size="medium"
                        variant="text"
                        onClick={sec.internal_report_path ? () => openConfirm(sec.currency, sec.breakdown_group, 'internal') : undefined}
                        disabled={!sec.internal_report_path}
                        sx={{ 
                          color: sec.internal_report_path ? COLORS.LINK_BLUE : 'error.main', 
                          textDecoration: 'underline', 
                          px: 0, 
                          minWidth: 0, 
                          '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                          '&.Mui-disabled': { color: 'error.main', textDecoration: 'underline' }
                        }}
                      >
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Card>
        );
      })}

      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Download Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2"><b>Payout Date:</b> {payoutDateString}</Typography>
            <Typography variant="body2"><b>Currency:</b> {confirm.currency}</Typography>
            {confirm.report_name && (
              <Typography variant="body2"><b>Report Name:</b> {confirm.report_name}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeConfirm} variant="outlined" sx={{ mr: 1 }}>No</Button>
          <Button 
            onClick={confirmDownload} 
            variant="contained"
            sx={{
              backgroundColor: COLORS.OXYGEN7_PURPLE,
              '&:hover': { backgroundColor: 'rgba(91, 45, 130, 0.8)' }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Details</Typography>
          <Typography variant="body2" color="text.secondary">
            Business Entity: {detailHeader.be} · Account: {detailHeader.account} · Currency: {detailHeader.currency}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total: {combinedRows.length} records · Settlements: {detailData.counts?.settlements || 0} · Adjustments: {detailData.counts?.adjustments || 0}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <CircularLoader />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Transaction Time</TableCell>
                    <TableCell>TID</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Merchant Name</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Account Number</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>Txn Amt</TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>Txn Fee</TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>MDR</TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}>MDR Amount</TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}>GST Amount</TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}>Paid Amount</TableCell>
                    <TableCell>Merchant Reference</TableCell>
                    <TableCell>Acquirer</TableCell>
                    <TableCell sx={{ minWidth: 500 }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {combinedRows.map((r) => {
                    const isAdj = r._variant === 'adjustment';
                    const txType = ((r.transaction_type || '') + '').toUpperCase();
                    const isRefundOrVoid = txType === 'REFUND' || txType === 'VOID';
                    const highlight = isAdj || isRefundOrVoid;
                    const mdr = r.merchant_mdr ?? 0;
                    const mdrAmount = r.merchant_mdr_amount ?? 0;
                    const gstAmount = r.merchant_gst_amount ?? 0;
                    const currency = r.transaction_currency || r.currency || '';
                    const isJPY = currency === 'JPY';
                    const numberFormat = isJPY ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
                    const mdrFormat = { minimumFractionDigits: 4, maximumFractionDigits: 4 };
                    return (
                      <TableRow key={`${r._variant}-${r.id}`} sx={highlight ? { '& td, & th': { color: 'error.main' } } : undefined}>
                        <TableCell>{r.transaction_id ?? ''}</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>{r.transaction_datetime ? moment.utc(r.transaction_datetime).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : ''}</TableCell>
                        <TableCell>{r.terminal_id ?? ''}</TableCell>
                        <TableCell sx={{ minWidth: 250, whiteSpace: 'normal', wordBreak: 'break-word' }}>{r.merchant_name ?? ''}</TableCell>
                        <TableCell>{r.account_number ?? ''}</TableCell>
                        <TableCell>{isAdj ? 'Adjustment' : (r.transaction_type ?? '')}</TableCell>
                        <TableCell>{isAdj ? 'SUCCESS' : (r.transaction_state ?? '')}</TableCell>
                        <TableCell>{r.payment_brand ?? ''}</TableCell>
                        <TableCell>{currency || ''}</TableCell>
                        <TableCell align="right">{(r.transaction_amount ?? r.total_amount) == null ? '' : Number(r.transaction_amount ?? r.total_amount).toLocaleString(undefined, numberFormat)}</TableCell>
                        <TableCell align="right">{(isAdj ? r.transaction_fee : r.txn_fee_fixed) == null ? '' : Number(isAdj ? r.transaction_fee : r.txn_fee_fixed).toLocaleString(undefined, numberFormat)}</TableCell>
                        <TableCell align="right">{mdr == null ? '' : Number(mdr).toLocaleString(undefined, mdrFormat)}</TableCell>
                        <TableCell align="right">{mdrAmount == null ? '' : Number(mdrAmount).toLocaleString(undefined, numberFormat)}</TableCell>
                        <TableCell align="right">{gstAmount == null ? '' : Number(gstAmount).toLocaleString(undefined, numberFormat)}</TableCell>
                        <TableCell align="right">{(isAdj ? (r.total_amount ?? r.paid_amount) : (r.paid_amount ?? r.total_paid_amount)) == null ? '' : Number(isAdj ? (r.total_amount ?? r.paid_amount) : (r.paid_amount ?? r.total_paid_amount)).toLocaleString(undefined, numberFormat)}</TableCell>
                        <TableCell>{r.gateway_reference_number ?? ''}</TableCell>
                        <TableCell>{r.acquirer ?? ''}</TableCell>
                        <TableCell sx={{ minWidth: 260 }}>
                          <ShowDetail text={r.details ?? ''} limit={50} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {combinedRows.length === 0 && (
                    <TableRow><TableCell colSpan={18}><Typography variant="body2" color="text.secondary">No data</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeDetail} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Outgoing;
